import React, { ReactNode, useCallback } from 'react';
import { useTitle } from 'react-use';
import { useLocation } from 'wouter';
import { useAppSelector } from '../../hooks';
import { currentUser } from '../../selectors';
import { c } from '../../utils';
import classes from './Toolbar.module.scss';
import { Clickable, ClickableVariant } from '../Clickable';
import { PlanBadge } from '../PlanBadge';

declare interface Props {
  title?: string;
  children?: ReactNode;
  classNames?: string;
  noPadding?: boolean;
  withInternalNav?: boolean;
}

const BASE_PAGE_TITLE = 'Event Manager | Gatherly';
function makePageTitle(title: string) {
  return `${title} - ${BASE_PAGE_TITLE}`;
}

export function Toolbar({
  title = '',
  children,
  classNames,
  noPadding,
  withInternalNav,
}: Props) {
  useTitle(makePageTitle(title));
  const user = useAppSelector(currentUser);

  const [, setLocation] = useLocation();

  // [2026-03-08] Redirects users based on their plan:
  // Free or Expired users go to Pricing for subscription; paid users go to the Account page for management.
  const handleActions = useCallback(() => {
    if (user?.role === 'free' || user?.role === 'expired') {
      // Navigate to the pricing page
      setLocation('/pricing');
    } else {
      // Redirect to your backend endpoint that creates a Stripe Portal session
      setLocation('/account');
    }
  }, [setLocation]);

  return (
    <div
      className={c(
        classes.Toolbar,
        classNames,
        withInternalNav && classes.withInternalNav,
        'flex items-center justify-between',
        't0 r0 fixed md:gap-1 z-3',
        !noPadding && 'p2',
      )}
    >
      <h1 className="subheading bold color-shade-80 nowrap overflow-ellipsis pr1">
        {title}
      </h1>
      {!children ? (
        // [2026-03-08] Show "Try a plan" for free or expired users; otherwise, display a badge for active subscriptions.
        user ? (
          user.role === 'free' || user.role === 'expired' ? (
            <Clickable
              variant={ClickableVariant.BUTTON_PRIMARY}
              label="Try a plan"
              onClick={handleActions}
            />
          ) : (
            <PlanBadge label={user?.role} />
          )
        ) : null
      ) : (
        <div className="flex-1 flex flex-wrap md:nowrap md:gap-1">
          {children}
        </div>
      )}
    </div>
  );
}
